import React from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Avatar, IconButton, Chip, Button, Stack,
} from "@mui/material";
import GavelIcon from "@mui/icons-material/Gavel";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

import { SECTION_COLORS } from "../../constants";
import { describeRuleSchedule, formatScheduleDate, RULE_SCHEDULE_BADGE_SX } from "../../utils/ruleSchedule";

const JsonBlock = ({ label, data }) => {
    if (!data || (Array.isArray(data) && data.length === 0)) return null;
    const isEmpty = typeof data === "object" && !Array.isArray(data) && Object.keys(data).length === 0;
    if (isEmpty) return null;
    return (
        <Box mt={1.5} p={1.5} sx={{ borderRadius: 2, bgcolor: "#fffbeb", border: "1px solid #fde68a" }}>
            <Typography fontSize={11} fontWeight={700} color={SECTION_COLORS.rules.main} mb={0.5}>{label}</Typography>
            <Typography component="pre" fontSize={11}
                sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap", wordBreak: "break-all", color: "#1a1a2e", m: 0 }}>
                {JSON.stringify(data, null, 2)}
            </Typography>
        </Box>
    );
};

const ViewRuleDialog = ({ open, rule, onClose, onEditRequest }) => {
    if (!rule) return null;

    const schedule = describeRuleSchedule(rule);
    const rows = [
        { label: "Nom de la règle", value: rule.name || "–" },
        { label: "Description", value: rule.description || "–" },
        {
            label: "Types de transaction",
            value: Array.isArray(rule.transaction_types) && rule.transaction_types.length > 0
                ? rule.transaction_types.join(", ")
                : rule.transaction_type,
        },
        {
            label: "Segments ciblés",
            value: Array.isArray(rule.segment_ids) && rule.segment_ids.length > 0
                ? `${rule.segment_ids.length} segment(s)`
                : "Tous les clients",
        },
        { label: "Période", value: schedule.label },
        { label: "Début d'exécution", value: rule.valid_from ? formatScheduleDate(rule.valid_from) : "—" },
        { label: "Fin d'exécution", value: rule.valid_until ? formatScheduleDate(rule.valid_until) : "—" },
        { label: "Priorité", value: rule.priority ?? 0 },
        { label: "Marque", value: rule.brand },
        { label: "Identifiant", value: rule.id },
        { label: "Créée le", value: rule.created_at ? new Date(rule.created_at).toLocaleDateString("fr-FR") : "–" },
    ];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ borderBottom: "2px solid #f1f3f5", px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, background: SECTION_COLORS.rules.gradient }}>
                        <GavelIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontFamily="Montserrat, sans-serif" fontWeight={700} color="#1a1a2e" fontSize={16}>
                            {rule.name || `Règle — ${rule.transaction_type}`}
                        </Typography>
                        <Typography fontSize={11} color="text.secondary">Détail de la règle</Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "24px !important", px: 3, pb: 2 }}>
                {/* Status banner */}
                <Box display="flex" alignItems="center" gap={1} mb={2.5} px={2} py={1.25}
                    sx={{
                        borderRadius: 2,
                        bgcolor: rule.active ? "#f0fdf4" : "#fef2f2",
                        border: `1px solid ${rule.active ? "#86efac" : "#fca5a5"}`
                    }}>
                    {rule.active
                        ? <CheckCircleIcon sx={{ fontSize: 18, color: "#16a34a" }} />
                        : <CancelIcon sx={{ fontSize: 18, color: "#DC2626" }} />}
                    <Typography fontSize={13} fontWeight={600} color={rule.active ? "#16a34a" : "#DC2626"}>
                        {rule.active ? "Active" : "Inactive"}
                    </Typography>
                    <Chip label={`Priorité ${rule.priority ?? 0}`} size="small"
                        sx={{
                            ml: "auto", fontWeight: 700, fontSize: 11,
                            bgcolor: SECTION_COLORS.rules.light, color: SECTION_COLORS.rules.main
                        }} />
                    {schedule.badge && (
                        <Chip
                            label={schedule.badge.text}
                            size="small"
                            sx={{ fontWeight: 700, fontSize: 11, ...RULE_SCHEDULE_BADGE_SX[schedule.badge.key] }}
                        />
                    )}
                </Box>

                {/* Meta rows */}
                <Stack spacing={1} mb={1}>
                    {rows.map(({ label, value }) => (
                        <Box key={label} display="flex" alignItems="flex-start" justifyContent="space-between"
                            px={2} py={1} sx={{ borderRadius: 2, bgcolor: "#fafbfd", border: "1px solid #e8ecf1" }}>
                            <Typography fontSize={12} color="text.secondary" fontWeight={500} sx={{ minWidth: 150 }}>{label}</Typography>
                            <Typography fontSize={12} fontWeight={600} color="#1a1a2e" sx={{ textAlign: "right", wordBreak: "break-all" }}>{value}</Typography>
                        </Box>
                    ))}
                </Stack>

                {/* JSON blocks */}
                <JsonBlock label="segment_ids" data={rule.segment_ids} />
                <JsonBlock label="conditions" data={rule.conditions} />
                <JsonBlock label="actions" data={rule.actions} />
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0", gap: 1 }}>
                <Button onClick={onClose} sx={{ color: "text.secondary", textTransform: "none" }}>Fermer</Button>
                <Button variant="contained" onClick={() => { onClose(); onEditRequest(rule); }}
                    startIcon={<EditIcon />}
                    sx={{ background: SECTION_COLORS.rules.gradient, textTransform: "none", borderRadius: 2, fontWeight: 600, px: 3 }}>
                    Modifier
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ViewRuleDialog;
